import React from 'react';
import { ArrowUp, ArrowDown } from 'react-feather';
import { FREQUENCY } from 'constants/options';
import {
  calcPercentagePremiumIncrease,
  prettifyNumber,
  convertFrequency,
} from 'helpers';
import styles from './renewalComparison.scss';

export interface IRenewalComparisonProps {
  className?: string;
  text?: React.ReactNode;
  isUpcoming: boolean;
  prevPremium: number;
  nextPremium: number;
  prevFrequency: string;
  nextFrequency: string;
  policyProviderName: string;
  getText?(isIncrease: boolean, value: string): React.ReactNode;
}

const SubText = ({ isIncrease, nextPremium, value }) => {
  const arrow = isIncrease ? (
    <ArrowUp className={styles.arrow} size={14} />
  ) : (
    <ArrowDown className={styles.arrow} size={14} />
  );

  return (
    <span className={styles.subText}>
      <span
        id="RenewalComparison_Premium_Change"
        className={styles.increaseText}
      >
        {isIncrease || !nextPremium ? 'INCREASE' : 'DECREASE'} OF{' '}
      </span>
      <span>{nextPremium ? value : '0%'}</span>
      {nextPremium > 0 && arrow}
    </span>
  );
};

const RenewalComparison = ({
  className,
  text,
  getText,
  prevPremium,
  nextPremium,
  prevFrequency,
  nextFrequency,
  isUpcoming,
  policyProviderName,
}: IRenewalComparisonProps) => {
  const { isIncrease, value } = calcPercentagePremiumIncrease(
    prevPremium,
    nextPremium,
    prevFrequency,
    nextFrequency,
  );

  const prevConverted = convertFrequency(
    prevPremium,
    prevFrequency,
    FREQUENCY.YEARLY,
    policyProviderName,
  );

  const nextConverted = convertFrequency(
    nextPremium,
    nextFrequency,
    FREQUENCY.YEARLY,
    policyProviderName,
  );

  const prev = isNaN(prevConverted)
    ? '-'
    : `$${prettifyNumber(prevConverted)}/yr`;

  const next = isNaN(nextConverted)
    ? '-'
    : `$${prettifyNumber(nextConverted)}/yr`;

  return (
    <div
      id="RenewalComparison"
      className={classnames(styles.comparison, className)}
    >
      <h2 id="RenewalComparison_Title" className={styles.title}>
        {getText ? getText(isIncrease, value) : text}
      </h2>
      <div id="RenewalComparison_Premium" className={styles.premiums}>
        {!isNaN(prevConverted) && (
          <div id="RenewalComparison_Premium_LastYear" className={styles.card}>
            {isUpcoming && <div className={styles.dot} />}
            <div className={styles.label}>
              {`${isUpcoming ? 'Current' : 'Previous'} premium`}
            </div>
            <div
              id="RenewalComparison_Premium_LastYear_Value"
              className={styles.value}
            >
              {prev}
            </div>
          </div>
        )}
        <div id="RenewalComparison_Premium_ThisYear" className={styles.card}>
          {!isUpcoming && <div className={styles.dot} />}
          <div className={styles.label}>{`${
            isUpcoming ? 'Upcoming' : 'Current'
          } premium`}</div>
          <div
            id="RenewalComparison_Premium_ThisYear_Value"
            className={styles.value}
          >
            {next}
            {value && (
              <SubText
                isIncrease={isIncrease}
                nextPremium={nextPremium}
                value={value}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewalComparison;
